import { useState } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useScopeParams } from "@/hooks/useScopeParams";
import { Button, Input, Select, Table, Typography, Switch } from "antd";
import type { TableColumnsType } from "antd";
import { ColumnVisibility, ColumnsType } from "@/types/tableState.types";
import ResizableDrawer from "@/components/UI/ResizableDrawer";

type ColumnRow = {
  key: string;
  fieldName: string;
};

const saveJSON = (name: string, data: unknown) => {
  const dataStr =
    "data:text/json;charset=utf-8," +
    encodeURIComponent(JSON.stringify(data, null, 2));
  const downloadAnchorNode = document.createElement("a");
  downloadAnchorNode.setAttribute("href", dataStr);
  downloadAnchorNode.setAttribute("download", name);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

const createColumnsConfig = (columns: string[]): ColumnsType[] => {
  return columns.map((column) => ({
    fieldName: column,
    visibility: ColumnVisibility.VISIBLE,
    mandatory: false,
  }));
};

type AdvancedColumnsDrawerProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  allColumns: string[];
  columnsTitleCreator: (fieldName: string) => string;
  pageName: string;
  fullPageName: string;
  enableColumnStateEditing?: boolean;
  columnsConfig?: ColumnsType[];
};

export const AdvancedColumnsDrawer = ({
  isOpen,
  setIsOpen,
  allColumns,
  columnsTitleCreator,
  pageName,
  fullPageName,
  enableColumnStateEditing = false,
  columnsConfig,
}: AdvancedColumnsDrawerProps) => {
  const { id } = useScopeParams();
  const initialDrawerWidth = enableColumnStateEditing ? "75%" : "50%";
  const [columnsTitle, setColumnsTitle] = useState({
    ...allColumns.reduce(
      (acc, column) => {
        acc[column] = columnsTitleCreator(column);
        return acc;
      },
      {} as Record<string, string>,
    ),
  });

  const [editedColumnsConfig, setEditedColumnsConfig] = useState<ColumnsType[]>(
    columnsConfig ?? createColumnsConfig(allColumns),
  );

  const isColumnStateEnabled =
    enableColumnStateEditing && !!columnsConfig?.length;

  const moveColumn = (fieldName: string, direction: "up" | "down") => {
    if (!isColumnStateEnabled) return;

    setEditedColumnsConfig((prev) => {
      const currentIndex = prev.findIndex(
        (column) => column.fieldName === fieldName,
      );
      if (currentIndex === -1) return prev;

      const nextIndex =
        direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;

      const updated = [...prev];
      [updated[currentIndex], updated[nextIndex]] = [
        updated[nextIndex],
        updated[currentIndex],
      ];
      return updated;
    });
  };

  const moveColumnToPosition = (fieldName: string, targetIndex: number) => {
    if (!isColumnStateEnabled) return;

    const column = editedColumnsConfig.find(
      (column) => column.fieldName === fieldName,
    );
    if (!column) return;

    setEditedColumnsConfig((prev) => {
      const currentIndex = prev.findIndex(
        (column) => column.fieldName === fieldName,
      );
      if (currentIndex === -1 || currentIndex === targetIndex) return prev;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const updated = [...prev];
      updated.splice(currentIndex, 1);
      updated.splice(targetIndex, 0, { ...column } as ColumnsType);
      return updated;
    });
  };

  const handleVisibilityChange = (
    fieldName: string,
    visibility: ColumnVisibility,
  ) => {
    if (!isColumnStateEnabled) return;

    setEditedColumnsConfig((prev) =>
      prev.map((column) =>
        column.fieldName === fieldName ? { ...column, visibility } : column,
      ),
    );
  };

  const handleMandatoryChange = (fieldName: string, mandatory: boolean) => {
    if (!isColumnStateEnabled) return;

    setEditedColumnsConfig((prev) =>
      prev.map((column) =>
        column.fieldName === fieldName ? { ...column, mandatory } : column,
      ),
    );
  };

  const handleDownloadView = () => {
    if (!isColumnStateEnabled) return;

    saveJSON(`${id ?? "unknownTrial"}-${fullPageName}-defaultView.json`, {
      columns: editedColumnsConfig,
    });
  };

  const baseTableColumns: TableColumnsType<ColumnRow> = [
    {
      title: "Field Name",
      dataIndex: "fieldName",
      key: "fieldName",
      width: isColumnStateEnabled ? "30%" : "45%",
      render: (fieldName: string) => (
        <Typography.Text code className="text-xs">
          {fieldName}
        </Typography.Text>
      ),
    },
    {
      title: "Display Title",
      key: "title",
      render: (_, record: ColumnRow) => (
        <Input
          size="small"
          allowClear
          placeholder="Enter a friendly label"
          value={columnsTitle[record.fieldName] ?? ""}
          onChange={(e) => {
            setColumnsTitle((prev) => ({
              ...prev,
              [record.fieldName]: e.target.value,
            }));
          }}
        />
      ),
    },
  ];

  const tableColumns: TableColumnsType<ColumnRow> = isColumnStateEnabled
    ? [
        {
          title: "Order",
          key: "order",
          width: "15%",
          render: (_, record: ColumnRow, index: number) => {
            const isFirst = index === 0;
            const isLast = index === editedColumnsConfig.length - 1;

            return (
              <div className="flex items-center gap-2">
                <Select
                  size="small"
                  style={{ width: 75 }}
                  value={index + 1}
                  options={editedColumnsConfig.map((_, index) => ({
                    label: index + 1,
                    value: index + 1,
                  }))}
                  onChange={(value: number) =>
                    moveColumnToPosition(record.fieldName, value - 1)
                  }
                />
                <div className="flex flex-col">
                  <Button
                    type="text"
                    size="small"
                    icon={<UpOutlined />}
                    aria-label={`Move ${record.fieldName} up`}
                    disabled={isFirst}
                    onClick={() => moveColumn(record.fieldName, "up")}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<DownOutlined />}
                    aria-label={`Move ${record.fieldName} down`}
                    disabled={isLast}
                    onClick={() => moveColumn(record.fieldName, "down")}
                  />
                </div>
              </div>
            );
          },
        },
        ...baseTableColumns,
        {
          title: "Visibility",
          key: "visibility",
          width: "20%",
          render: (_, record: ColumnRow) => (
            <Select
              size="small"
              style={{ width: "100%" }}
              value={
                editedColumnsConfig.find(
                  (column) => column.fieldName === record.fieldName,
                )?.visibility
              }
              options={[
                { label: "Visible", value: ColumnVisibility.VISIBLE },
                {
                  label: "Not available",
                  value: ColumnVisibility.NOT_AVAILABLE,
                },
              ]}
              onChange={(value) =>
                handleVisibilityChange(
                  record.fieldName,
                  value as ColumnVisibility,
                )
              }
            />
          ),
        },
        {
          title: "Locked",
          key: "locked",
          width: "15%",
          render: (_, record: ColumnRow) => {
            return (
              <Switch
                checked={
                  editedColumnsConfig.find(
                    (column) => column.fieldName === record.fieldName,
                  )?.mandatory ?? false
                }
                onChange={(value) =>
                  handleMandatoryChange(record.fieldName, value)
                }
              />
            );
          },
        },
      ]
    : baseTableColumns;

  const orderedDataSource: ColumnRow[] = editedColumnsConfig.map((column) => ({
    key: column.fieldName,
    fieldName: column.fieldName,
  }));

  return (
    <ResizableDrawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      headerTitle={
        <span className="text-[16px] font-semibold">Columns Configuration</span>
      }
      sizeToggle={{
        collapsedWidth: "50%",
        expandedWidth: "75%",
        initialWidth: initialDrawerWidth,
      }}
      closable={false}
      closeIcon={null}
    >
      <div className="flex flex-col gap-2 mb-4">
        <Typography.Text strong>Column configuration</Typography.Text>
        <Typography.Text type="secondary">
          Please note that changes performed here won&apos;t be saved until
          Front-end team apply them. Send the updated dictionary to the
          Front-end team for implementation.
        </Typography.Text>
      </div>
      <div className="flex justify-end gap-2 mb-4">
        {isColumnStateEnabled && (
          <Button onClick={handleDownloadView}>Download View</Button>
        )}
        <Button
          type="primary"
          onClick={() => {
            saveJSON(`${pageName}-dictionary.json`, columnsTitle);
          }}
        >
          Save Dictionary
        </Button>
      </div>
      <Table
        size="small"
        bordered
        pagination={false}
        dataSource={orderedDataSource}
        columns={tableColumns}
      />
    </ResizableDrawer>
  );
};
